import React, { useState, useEffect, useContext, useRef } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  useFilters,
} from "react-table";
import "bootstrap/dist/css/bootstrap.min.css";
// import "./ExpandableTable.css"; // Reuse if needed

import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { ThemeContext } from "../../../..";
import SvgSortdownarrow from "../../../../Components/IconComponents/Sortdownarrow";
import SvgSortuparrow from "../../../../Components/IconComponents/Sortuparrow";
import SvgSorting from "../../../../Components/IconComponents/Sortingarrow";
import TableHeader from "../../../../Components/UiComponents/Table/TableHeader/TableHeader";
import { CheckCircle, XCircle } from "../../../../Components/IconComponents";
import { ChevronDown, ChevronRight } from "lucide-react";

const SOPExpandableTable = (props) => {
  const {
    columns,
    data,
    numberOfRows,
    headerTableHeadStyle = {},
    tableStyle = {},
    showRowCount = true,
    rowCount,
    searchData,
    setSearchData = () => {},
    enableColumnFilters = false,
    showAutoManual = true,
    showResourceQuantities = false,
    chevronToggle = false,
    expandFirstColLabel = "S. No.",
    expandFirstColKey = null,
    hideExpandFirstCol = false,
    markDeletedRows = true,
  } = props;
 
  const [expandedRowIds, setExpandedRowIds] = useState({});

  // Height of the main table's header row — the sub-table header sticks
  // just below it (top: mainHeaderHeight) instead of at top: 0, so the two
  // headers stack instead of colliding when both are pinned at once.
  const mainHeaderRowRef = useRef(null);
  const [mainHeaderHeight, setMainHeaderHeight] = useState(41);

  useEffect(() => {
    if (mainHeaderRowRef.current) {
      setMainHeaderHeight(mainHeaderRowRef.current.getBoundingClientRect().height);
    }
  }, [columns, headerTableHeadStyle]);

  const toggleRowExpansion = (rowId) => {
    setExpandedRowIds((prev) => {
      const isCurrentlyExpanded = !!prev[rowId];
      // Only allow one row expanded at a time (standard pattern in this app it seems)
      return isCurrentlyExpanded ? {} : { [rowId]: true };
    });
  };
 
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    rows,
    state: { pageIndex, globalFilter, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: numberOfRows },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );
 
  const { t } = useTranslation();
  const { theme } = useContext(ThemeContext);
 
  useEffect(() => {
    setPageSize(numberOfRows);
  }, [numberOfRows]);
 
  useEffect(() => {
    if (globalFilter) {
      const pageData = page?.map((row) => row.original);
      setSearchData(pageData ? pageData : []);
    } else {
      const pageData = rows?.map((row) => row.original);
      setSearchData(pageData ? pageData : []);
    }
  }, [globalFilter, rows]);
 
  const renderExpandedRow = (row) => {
    const resources = row.original.resources || [];
    let colWidths =
      showAutoManual && showResourceQuantities
        ? ["5%", "19%", "10%", "10%", "10%", "10%", "12%", "12%", "12%"]
        : showAutoManual
        ? ["6%", "36%", "19%", "19%", "20%"]
        : showResourceQuantities
        ? ["6%", "24%", "14%", "14%", "14%", "14%", "14%"]
        : ["6%", "64%", "30%"];
    // A first column carrying an id (e.g. Matrix ID) needs more room than "S. No."
    if (expandFirstColKey && colWidths.length > 1) {
      colWidths[0] = "14%";
      colWidths[1] = showResourceQuantities ? "16%" : "54%";
    }
    // First column dropped entirely — fold its width into Resource Name
    // instead of leaving a blank/serial-number column in its place.
    if (hideExpandFirstCol && colWidths.length > 1) {
      const [firstWidth, secondWidth, ...rest] = colWidths;
      colWidths = [`${parseFloat(firstWidth) + parseFloat(secondWidth)}%`, ...rest];
    }
    const expandColCount = colWidths.length;

    // Sub-header sticks within the SAME scroll container as the main table
    // header (.iccc__table--container), offset by the main header's own
    // height so the two stack instead of colliding at top:0. It stays
    // pinned below the main header for as long as any of this row's
    // resources are still in view, and scrolls away once they're all
    // scrolled past — matching the main header's sticky behavior.
    const subHeaderThStyle = {
      backgroundColor: "#1C4E72",
      border: "none",
      color: "#fff",
      position: "sticky",
      top: `${mainHeaderHeight}px`,
      zIndex: 1,
    };

    return (
      <tr style={{ backgroundColor: "#f0f8ff" }}>
        <td colSpan={columns.length + 1} style={{ padding: "10px 16px 14px" }}>
          <div
            style={{
              width: "100%",
              borderRadius: "8px",
              background: "white",
              padding: "0px",
              border: "1px solid #000",
              // No `overflow: hidden` here — that would make this div the
              // nearest scroll/clip ancestor for the sticky `th`s below,
              // trapping their sticky offset inside a box that never
              // actually scrolls, instead of the outer table's scroller.
            }}
          >
            <table className="table table-sm mb-0" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%" }}>
              <colgroup>
                {colWidths.map((w, idx) => (
                  <col key={idx} style={{ width: w }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {!hideExpandFirstCol && (
                    <th className="text-center py-2" style={{ ...subHeaderThStyle, borderTopLeftRadius: "7px", fontStyle: "normal" }}>{expandFirstColLabel}</th>
                  )}
                  <th
                    className="ps-2 py-2"
                    style={{ ...subHeaderThStyle, ...(hideExpandFirstCol ? { borderTopLeftRadius: "7px" } : {}) }}
                  >
                    {"Resource Name"}
                  </th>
                  {showResourceQuantities && (
                    <>
                      <th className="text-center py-2" style={{ ...subHeaderThStyle, fontStyle: "normal" }}>{"Available Qty"}</th>
                      <th className="text-center py-2" style={{ ...subHeaderThStyle, fontStyle: "normal" }}>{"Damaged Qty"}</th>
                      <th className="text-center py-2" style={{ ...subHeaderThStyle, fontStyle: "normal" }}>{"Deployed Qty"}</th>
                      <th className="text-center py-2" style={{ ...subHeaderThStyle, fontStyle: "normal" }}>{"Total Qty"}</th>
                    </>
                  )}
                  <th className="text-center py-2" style={{ ...subHeaderThStyle, fontStyle: "normal", borderTopRightRadius: showAutoManual ? undefined : "7px" }}>{"Qty To Deploy"}</th>
                  {showAutoManual && (
                    <>
                      <th className="text-center py-2" style={subHeaderThStyle}>{"Auto"}</th>
                      <th className="text-center py-2" style={{ ...subHeaderThStyle, borderTopRightRadius: "7px" }}>{"Manual"}</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {resources.length > 0 ? (
                  resources.map((resource, idx) => {
                    const isLast = idx === resources.length - 1;
                    const rowBorderBottom = isLast ? "none" : "1px solid #dee2e6";
                    return (
                    <tr key={idx} className="align-middle">
                      {!hideExpandFirstCol && (
                        <td className="text-center" style={{ fontWeight: "400", fontSize: "11px", fontStyle: "normal", borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: rowBorderBottom, borderBottomLeftRadius: isLast ? "7px" : undefined }}>{expandFirstColKey ? (resource[expandFirstColKey] ?? "NA") : idx + 1}</td>
                      )}
                      <td className="ps-3 text-start" style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: rowBorderBottom, borderBottomLeftRadius: hideExpandFirstCol && isLast ? "7px" : undefined }}>
                        <div style={{ fontWeight: "400", fontSize: "11px", fontStyle: "normal" }}>{resource.resource_name || "NA"}</div>
                      </td>
                      {showResourceQuantities && (
                        <>
                          <td className="text-center" style={{ fontWeight: "400", fontSize: "11px", fontStyle: "normal", borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: rowBorderBottom }}>{resource.cum_available_quantity ?? resource.available_quantity ?? "0"}</td>
                          <td className="text-center" style={{ fontWeight: "400", fontSize: "11px", fontStyle: "normal", borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: rowBorderBottom }}>{resource.cum_damaged_quantity ?? "0"}</td>
                          <td className="text-center" style={{ fontWeight: "400", fontSize: "11px", fontStyle: "normal", borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: rowBorderBottom }}>{resource.cum_deployed_quantity ?? "0"}</td>
                          <td className="text-center" style={{ fontWeight: "400", fontSize: "11px", fontStyle: "normal", borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: rowBorderBottom }}>{resource.cum_total_quantity ?? "0"}</td>
                        </>
                      )}
                      <td className="text-center" style={{ fontWeight: "400", fontSize: "11px", fontStyle: "normal", borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: rowBorderBottom, borderBottomRightRadius: isLast && !showAutoManual ? "7px" : undefined }}>{resource.quantity ?? resource.required_quantity ?? "0"}</td>
                      {showAutoManual && (
                        <>
                          <td className="text-center" style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: rowBorderBottom }}>
                            { (resource.is_automatic === true || resource.is_automatic === 1 || resource.is_automatic === "true") ? (
                              <CheckCircle style={{ color: "#28a745", width: "18px", height: "18px" }} />
                            ) : (
                              <XCircle style={{ color: "#dc3545", width: "18px", height: "18px" }} />
                            )}
                          </td>
                          <td className="text-center" style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: rowBorderBottom, borderBottomRightRadius: isLast ? "7px" : undefined }}>
                            { (resource.is_manual === true || resource.is_manual === 1 || resource.is_manual === "true") ? (
                              <CheckCircle style={{ color: "#28a745", width: "18px", height: "18px" }} />
                            ) : (
                              <XCircle style={{ color: "#dc3545", width: "18px", height: "18px" }} />
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={expandColCount} className="text-center text-muted py-3" style={{ border: "none" }}>{"No resources found"}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </td>
      </tr>
    );
  };
 
  return (
    <div className="tabl_container">
      <TableHeader
        {...props}
        searchValue={globalFilter || ""}
        onSearchHandler={setGlobalFilter}
      />
      {props.afterHeaderContent}
      <style>{`
        /* Child combinators (not descendant selectors) so these rules only
           touch the outer table's own rows — the expanded "resources"
           sub-table nested inside a td keeps its own border/styling. */
        .sop-expandable-table > table > tbody > tr > td,
        .sop-expandable-table > table > tbody > tr > th {
          border: none !important;
        }
        .sop-expandable-table > table > tbody > tr > td {
          background-color: #fff !important;
          color: #1D2939 !important;
        }
        .sop-expandable-table > table > tbody > tr:nth-child(even) > td {
          background-color: #F9FAFB !important;
        }
        /* Deleted matrix config — greyed out in the main table */
        .sop-expandable-table > table > tbody > tr.icm-deleted-row > td {
          background-color: #f1f3f5 !important;
          color: #9aa4ae !important;
        }
        /* Fade the category / severity badges (direct-child spans only, not action icons) */
        .sop-expandable-table > table > tbody > tr.icm-deleted-row > td > span[style] {
          color: #9aa4ae !important;
          background: #e4e7eb !important;
          border-color: transparent !important;
        }
        /* Keep the expand chevron and the view / audit-history icons fully active
           on deleted rows — only edit & delete are disabled. */
        .sop-expandable-table > table > tbody > tr.icm-deleted-row td .icm-keep-active,
        .sop-expandable-table > table > tbody > tr.icm-deleted-row td .icm-keep-active svg {
          color: #1D2939 !important;
          opacity: 1 !important;
        }
        .sop-expandable-table > table > tbody > tr.icm-deleted-row > td:first-child,
        .sop-expandable-table > table > tbody > tr.icm-deleted-row > td:first-child svg {
          color: #2E6DA4 !important;
          opacity: 1 !important;
        }
      `}</style>
      <div className="iccc__table--container sop-expandable-table" style={tableStyle}>
        <table {...getTableProps()} className="table iccc--table">
          <thead>
            {headerGroups.map((headerGroup, idx) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={idx} ref={idx === 0 ? mainHeaderRowRef : undefined}>
                <th style={{ width: "45px", backgroundColor: "#1C4E72", color: "white", textAlign: "center", border: "none", zIndex: 2, ...headerTableHeadStyle }}></th>
                {headerGroup.headers.map((column, cidx) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    style={{
                        backgroundColor: '#18a3a0',
                        color: '#FFFFFF',
                        border: 'none',
                        padding: '12px 8px',
                        verticalAlign: 'middle',
                        textAlign: 'left',
                        zIndex: 2,
                        ...headerTableHeadStyle,
                    }}
                    key={cidx}
                  >
                    {column.render("Header")}
                    {!column.disableSortBy &&
                      (column.isSorted ? (
                        column.isSortedDesc ? (
                          <SvgSortdownarrow />
                        ) : (
                          <SvgSortuparrow />
                        )
                      ) : (
                        <SvgSorting />
                      ))}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length > 0 ? (
              page.map((row, i) => {
                prepareRow(row);
                const isExpanded = expandedRowIds[row.id];
 
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      {...row.getRowProps()}
                      className={`${isExpanded ? "table-light" : ""}${
                        row.original.isDeleted && markDeletedRows ? " icm-deleted-row" : ""
                      }`}
                      title={
                        row.original.isDeleted && markDeletedRows
                          ? "This matrix configuration has been deleted"
                          : undefined
                      }
                    >
                      <td className="text-center p-0">
                        <div
                          onClick={() => toggleRowExpansion(row.id)}
                          style={{
                            cursor: "pointer",
                            fontSize: "16px",
                            fontWeight: "400",
                            fontStyle: "normal",
                            color: "#333",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%"
                          }}
                        >
                          {chevronToggle ? (
                            isExpanded ? (
                              <ChevronDown size={16} color="#2E6DA4" />
                            ) : (
                              <ChevronRight size={16} color="#2E6DA4" />
                            )
                          ) : isExpanded ? (
                            "−"
                          ) : (
                            "+"
                          )}
                        </div>
                      </td>
                      {row.cells.map((cell, cidx) => (
                        <td
                          {...cell.getCellProps()}
                          key={cidx}
                          style={{ textAlign: "left", verticalAlign: "middle" }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                    {isExpanded && renderExpandedRow(row)}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length + 1} className="no-data-text text-center">
                  No data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
 
      <div className="d-flex justify-content-between align-items-center">
        <div className="float-start m-2 mt-2 ms-3 pagecount-style">
          {t("showingentriestable.showing")} {pageIndex * pageSize + 1} to{" "}
          {Math.min((pageIndex + 1) * pageSize, rowCount || data.length)} of{" "}
          {rowCount || data.length} {t("showingentriestable.entries")}
        </div>
        <div className="pagination">
          <div className="d-flex">
            <span className="pagination_gotopage">
              Go to page:
              <input
                type="number"
                min="1"
                defaultValue={pageIndex + 1}
                onChange={(e) => {
                  let pageNumber = Number(e.target.value);
                  if (pageNumber < 1 || isNaN(pageNumber)) {
                    pageNumber = 1;
                  }
                  gotoPage(pageNumber - 1);
                }}
                className="pagination_gotopage-input"
              />
            </span>
            <span>
              <select
                className="pagination_select"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {[10, 50, 100, 500, 1000, 2000].map((size) => (
                  <option
                    key={size}
                    value={size}
                    style={{
                      background: theme === "light" ? "white" : "#252b48",
                      color: theme === "light" ? "#2b6591" : "#ffffff",
                    }}
                  >
                    show {size}
                  </option>
                ))}
              </select>
            </span>
          </div>
          <button
            className="pagebtn mx-2"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            {"<"}
          </button>
          <button className="pagination_page">{pageIndex + 1}</button>
          <button
            className="pagebtn ms-2"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            {">"}
          </button>
          <span className="right_pageno">
            {t("pagination.page_of", {
              page: pageIndex + 1,
              total: pageOptions.length,
            })}
          </span>
        </div>
      </div>
    </div>
  );
};
 
export default SOPExpandableTable;
